/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import axios, { AxiosResponse } from 'axios';
import { decode } from 'base-64';
import type {
  AlgorithmStep,
  AlgorithmName,
  SSRDataJson,
  SSRData,
  Headers,
  UrlToJWTInstance,
} from '../types';
import { hasJwtExpired } from '@airasia-rn/account/managers';

const ALGORITHMS: Record<AlgorithmName, AlgorithmStep[]> = {
  ALGO_ONE: ['BASE64', 'SPLIT_INTO_FOUR', 'REVERSE', 'INSERT_RANDOM_AT_3'],
  ALGO_TWO: [
    'BASE64',
    'RAIL_FENCE_CIPHER',
    'SPLIT_INTO_THREE',
    'ADD_EXTRA_CHAR',
  ],
  ALGO_THREE: ['BASE64', '3DES', 'SPLIT_INTO_FOUR', 'REVERSE'],
  ALGO_FOUR: [
    'BASE64',
    'COL_TRANSPOSITION_ENCODE',
    'SPLIT_INTO_FOUR',
    'REVERSE',
  ],
  ALGO_FIVE: ['BASE64', 'XOR_ENCRYPT', 'SPLIT_INTO_FIVE', 'ADD_EXTRA_CHAR'],
  ALGO_SIX: ['BASE64', 'XOR_ENCRYPT', 'SPLIT_INTO_FIVE', 'ADD_EXTRA_CHAR'],
};

const BASE64 = 'BASE64';
const REVERSE = 'REVERSE';
const ADD_EXTRA_CHAR = 'ADD_EXTRA_CHAR';
const SPLIT_INTO_THREE = 'SPLIT_INTO_THREE';
const SPLIT_INTO_FOUR = 'SPLIT_INTO_FOUR';
const INSERT_RANDOM_AT_3 = 'INSERT_RANDOM_AT_3';
const RAIL_FENCE_CIPHER = 'RAIL_FENCE_CIPHER';
const TRIPLEDES = '3DES';
const COL_TRANSPOSITION_ENCODE = 'COL_TRANSPOSITION_ENCODE';
const XOR_ENCRYPT = 'XOR_ENCRYPT';
const SPLIT_INTO_FIVE = 'SPLIT_INTO_FIVE';

const ALGORITHM_INDEX = 3;

const headers: Headers = {
  'Content-Type': 'application/json',
  channel_hash: '',
};

const ssrDataJson: SSRDataJson = {
  synonyms: [
    {
      name: 'ALGO_ONE',
      value: ['SSI5', 'SPAK', 'SMD7', 'SKLB', 'SXAI'],
    },
    {
      name: 'ALGO_TWO',
      value: ['SRX5', 'SRDJ', 'SLAK', 'SKD7', 'SBZ2'],
    },
    {
      name: 'ALGO_THREE',
      value: ['SPGR', 'SMDJ', 'SMXT', 'SZQZ', 'SLXT'],
    },
    {
      name: 'ALGO_FOUR',
      value: ['SSXT', 'SLZ2', 'SRXT', 'SAI5', 'SBI5'],
    },
    {
      name: 'ALGO_FIVE',
      value: ['SLD7', 'SQXJ', 'SOXT', 'SBFD', 'SYXT'],
    },
  ],
  api_data: [
    {
      source: 'validation',
      id: 'yuyt0i39',
    },
    {
      source: 'traveldoc',
      id: 'ntoc3fj4',
    },
    {
      source: 'autoseat',
      id: 'kzsas23k',
    },
    {
      source: 'addons',
      id: 'lmdok7d1',
    },
    {
      source: 'seat',
      id: 'w4gfhjt2',
    },
    {
      source: 'checkout',
      id: 'qmzp52u2',
    },
    {
      source: 'checkinchain',
      id: 'hj78hja3',
    },
  ],
};

const findSRRSource = (
  ssrDataJson: SSRDataJson,
  ssrTknName: string,
): string | undefined => {
  const apiSource = ssrDataJson.api_data.find(
    (api) => api.source === ssrTknName,
  );
  if (!apiSource) return;
  return apiSource.id;
};

const identifyAlgorithm = (
  algorithmAlias: string,
  ssrDataJson: SSRDataJson,
): string => {
  if (!algorithmAlias || !ssrDataJson || !ssrDataJson.synonyms) {
    return '';
  }

  const algoSynonyms = ssrDataJson.synonyms;

  for (const synonym of algoSynonyms) {
    if (!synonym) continue;

    const synonymAlgo = synonym.value.find((algo) => algo === algorithmAlias);
    if (synonymAlgo) {
      return synonym.name;
    }
  }

  return '';
};

const decode64 = (input: string): string => {
  const keyStr =
    'ABCDEFGHIJKLMNOP' +
    'QRSTUVWXYZabcdef' +
    'ghijklmnopqrstuv' +
    'wxyz0123456789+/' +
    '=';
  let output = '';
  let chr1, chr2, chr3;
  let enc1, enc2, enc3, enc4;
  let i = 0;

  const base64test = /[^A-Za-z0-9+/=]/g;
  if (base64test.exec(input)) {
    alert(
      'There were invalid base64 characters in the input text.\n' +
        "Valid base64 characters are A-Z, a-z, 0-9, '+', '/',and '='\n" +
        'Expect errors in decoding.',
    );
  }
  input = input.replace(/[^A-Za-z0-9+/=]/g, '$$$');

  do {
    enc1 = keyStr.indexOf(input.charAt(i++));
    enc2 = keyStr.indexOf(input.charAt(i++));
    enc3 = keyStr.indexOf(input.charAt(i++));
    enc4 = keyStr.indexOf(input.charAt(i++));

    chr1 = (enc1 << 2) | (enc2 >> 4);
    chr2 = ((enc2 & 15) << 4) | (enc3 >> 2);
    chr3 = ((enc3 & 3) << 6) | enc4;

    output = output + String.fromCharCode(chr1);

    if (enc3 != 64) {
      output = output + String.fromCharCode(chr2);
    }
    if (enc4 != 64) {
      output = output + String.fromCharCode(chr3);
    }

    chr1 = chr2 = chr3 = '';
    enc1 = enc2 = enc3 = enc4 = '';
  } while (i < input.length);

  return unescape(output);
};

const reverseArray = <T>(ssrlist: T[]): T[] => {
  if (!ssrlist) return [];
  return ssrlist.slice().reverse();
};

const concatArrayElements = (ssrlist: string[]): string => {
  if (!ssrlist) return '';
  return ssrlist.join('');
};

const removeExtraChar = (ssrlist: string[]): string[] => {
  if (!ssrlist) return [];

  const removedExtraCharSSRList: string[] = [];
  ssrlist.forEach((ssr) => {
    if (!ssr) return;
    const removedCharSSR = ssr.substring(0, ssr.length - 1);
    removedExtraCharSSRList.push(removedCharSSR);
  });

  return removedExtraCharSSRList;
};

const removeRandomStringAtXPlace = (
  ssrlist: string[],
  xPlace: number,
): string[] | undefined => {
  if (!ssrlist) return;

  const removedExtraFromSSRList: string[] = [];
  for (let i = 0; i < ssrlist.length; i++) {
    if ((i + 1) % xPlace !== 0) {
      removedExtraFromSSRList.push(ssrlist[i]);
    }
  }

  return removedExtraFromSSRList;
};

const xorDecrypt = (value: string, xorKey: string): string => {
  let decryptedStr = '';
  const len = xorKey.length;
  const newValue = decode(value);

  for (let index = 0; index < newValue.length; index++) {
    decryptedStr += String.fromCharCode(
      newValue.charCodeAt(index) ^ xorKey.charCodeAt(index % len),
    );
  }

  return decryptedStr;
};

const reverseXChar = (value: string, key: number): string => {
  let newValue = '';
  for (let index = 0; index < value.length; index = index + key) {
    newValue += value
      .substring(index, index + key)
      .split('')
      .reverse()
      .join('');
  }
  return newValue;
};

const tripleDesDecrypt = (value: string): string => {
  const key = [10, 4, 15];
  return reverseXChar(
    reverseXChar(reverseXChar(value, key[2]), key[1]),
    key[0],
  );
};

const railFenceCipher = (cipher: string, key: number): string => {
  const rail = new Array(key);
  for (let i = 0; i < key; i++) {
    rail[i] = new Array(cipher.length);
  }

  for (let i = 0; i < key; i++) {
    for (let j = 0; j < cipher.length; j++) {
      rail[i][j] = '\n';
    }
  }

  let dir_down;
  let row = 0,
    col = 0;

  for (let i = 0; i < cipher.length; i++) {
    if (row == 0) dir_down = true;
    if (row == key - 1) dir_down = false;

    rail[row][col++] = '*';
    if (dir_down) {
      row++;
    } else {
      row--;
    }
  }

  let index = 0;
  for (let i = 0; i < key; i++) {
    for (let j = 0; j < cipher.length; j++) {
      if (rail[i][j] == '*' && index < cipher.length) {
        rail[i][j] = cipher[index++];
      }
    }
  }

  let result = '';
  row = 0;
  col = 0;

  for (let i = 0; i < cipher.length; i++) {
    if (row == 0) dir_down = true;
    if (row == key - 1) dir_down = false;

    if (rail[row][col] != '*') {
      result = rail[row][col++] + result;
    }

    if (dir_down) {
      row++;
    } else {
      row--;
    }
  }

  return result.split('').reverse().join('');
};

const columnTranspositionDecode = (
  ciphertext: string,
  keyword: string,
): string => {
  const klen = keyword.length;
  const cols = new Array(klen);
  const colLength = ciphertext.length / klen;

  for (let i = 0; i < klen; i++) {
    cols[i] = ciphertext.substr(i * colLength, colLength);
  }

  const newcols = new Array(klen);
  let j = 0;
  let i = 0;
  const chars = 'abcdefghijklmnopqrstuvwxyz';

  while (j < klen) {
    const t = keyword.indexOf(chars.charAt(i));
    if (t >= 0) {
      newcols[t] = cols[j++];
      const arrkw = keyword.split('');
      arrkw[t] = '_';
      keyword = arrkw.join('');
    } else {
      i++;
    }
  }

  let plaintext = '';
  for (i = 0; i < colLength; i++) {
    for (j = 0; j < klen; j++) {
      plaintext += newcols[j].charAt(i);
    }
  }

  return plaintext.replace(/@/g, '');
};

const executeAlgorithm = (
  algorithmOrder: AlgorithmStep[],
  ssrlist: string | string[],
  xorKey: string,
): string | undefined => {
  if (!algorithmOrder || !ssrlist) return;

  let newSSRValue: string | string[] | undefined;

  const start = (index: number, value: string | string[]): void => {
    switch (algorithmOrder[index]) {
      case BASE64:
        newSSRValue = decode64(value as string);
        break;
      case REVERSE:
        newSSRValue = reverseArray(value as string[]);
        break;
      case ADD_EXTRA_CHAR:
        newSSRValue = removeExtraChar(value as string[]);
        break;
      case SPLIT_INTO_THREE:
      case SPLIT_INTO_FOUR:
      case SPLIT_INTO_FIVE:
        newSSRValue = concatArrayElements(value as string[]);
        break;
      case INSERT_RANDOM_AT_3:
        newSSRValue = removeRandomStringAtXPlace(
          value as string[],
          ALGORITHM_INDEX,
        );
        break;
      case XOR_ENCRYPT:
        newSSRValue = xorDecrypt(value as string, xorKey);
        break;
      case TRIPLEDES:
        newSSRValue = tripleDesDecrypt(value as string);
        break;
      case RAIL_FENCE_CIPHER:
        newSSRValue = railFenceCipher(value as string, 7);
        break;
      case COL_TRANSPOSITION_ENCODE:
        newSSRValue = columnTranspositionDecode(value as string, 'depart');
        break;
      default:
        break;
    }

    index++;
    if (algorithmOrder.length > index && newSSRValue !== undefined) {
      start(index, newSSRValue);
    }
  };

  start(0, ssrlist);
  return `${newSSRValue}`;
};

const processSingleSSRData = (
  ssrData: SSRData | null,
  ssrDataJson: SSRDataJson,
  ssrTknName: string,
  xorKey: string,
): string | undefined => {
  if (!ssrData || !ssrDataJson || !ssrTknName) return;

  const source = findSRRSource(ssrDataJson, ssrTknName);
  if (!source) return;

  const ssr = ssrData?.ssr_data?.find((ssr) => ssr.source === source);
  if (!ssr) return;

  const algorithmUsed = identifyAlgorithm(
    ssr.ssrgroup,
    ssrDataJson,
  ) as AlgorithmName;
  if (!ALGORITHMS[algorithmUsed]) return;

  const algo = [...ALGORITHMS[algorithmUsed]];
  const reversedAlgo = reverseArray(algo);
  if (!reversedAlgo) return;

  const decryptedSSRData = executeAlgorithm(reversedAlgo, ssr.ssrlist, xorKey);
  if (!decryptedSSRData) return;

  return decryptedSSRData;
};

const STALE_TIME = 10 * 60 * 1000; // 10 minutes

export const createUrlToJWT = (
  authUrl: string,
  channelHash: string,
  ssrData: SSRDataJson,
  xorKey: string,
): UrlToJWTInstance => {
  let authData: SSRData | null = null;
  let lastFetchTime: number | null = null;

  headers.channel_hash = channelHash;
  ssrDataJson.api_data = ssrData.api_data;
  ssrDataJson.synonyms = ssrData.synonyms;

  const getAuthData = (): Promise<AxiosResponse<SSRData>> => {
    return axios.post<SSRData>(authUrl, {}, { headers });
  };

  const getJWTToken = (requestUrl: string): string | undefined => {
    return processSingleSSRData(authData, ssrDataJson, requestUrl, xorKey);
  };

  const setAuthDataPromise = (
    requestUrl: string,
  ): Promise<string | undefined> => {
    return getAuthData()
      .then((response) => {
        authData = response.data;
        lastFetchTime = Date.now();
        return authData;
      })
      .then(() => {
        return getJWTToken(requestUrl);
      })
      .catch((error) => {
        console.error('Auth request failed:', error);
        // Error handling without console.log
        return undefined;
      });
  };

  const getToken = (
    requestUrl: string,
    forceRefresh?: boolean,
  ): Promise<string | undefined> => {
    const now = Date.now();
    const age = lastFetchTime ? now - lastFetchTime : null;
    const isStale = age !== null && age > STALE_TIME;

    if (authData && !forceRefresh && !isStale) {
      const token = getJWTToken(requestUrl);
      if (token && !hasJwtExpired(token)) {
        return Promise.resolve(token);
      }
    }

    // If we're here, it means either we don't have authData,
    // it's stale/forced, or the produced token is expired.
    authData = null;
    return setAuthDataPromise(requestUrl);
  };

  return {
    getToken,
    getAuthData,
    getJWTToken,
  };
};
